import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { Check, Loader2, PackageSearch, Plus, Tag, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { PromotionApplicabilitySheet } from './PromotionApplicabilitySheet'
import { PromotionConflictDetails } from './PromotionConflictDetails'
import {
  emptyPromotionForm,
  promotionFormSchema,
  promotionFormToPayload,
  promotionToFormValues,
  type PromotionFormValues,
} from './promotion-form'

import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import { FormError } from '@/app/components/ui/FormError'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Checkbox } from '@/app/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { Textarea } from '@/app/components/ui/textarea'
import {
  type PromotionApiError,
  type PromotionConflict,
  type PromotionOption,
  promotionService,
} from '@/app/services/promotion.service'
import { sucursalService } from '@/app/services/sucursal.service'
import { useSessionStore } from '@/app/store/auth/auth.store'
import type { CatalogItem } from '@/hooks/useCatalogSearch'

interface PromotionFormModalProps {
  open: boolean
  promotionId: number | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

interface ProductPrice {
  final_price?: string | number | null
  sale_price?: string | number | null
}

function formatPromotionProductOption(item: CatalogItem): string {
  const name = item.name || item.title || `Producto #${item.id}`
  const sku = typeof item.sku === 'string' && item.sku ? item.sku : null
  const price = item.price as ProductPrice | undefined
  const rawPrice = price?.final_price ?? price?.sale_price
  const numericPrice = Number(rawPrice)
  const formattedPrice =
    rawPrice !== null &&
    rawPrice !== undefined &&
    Number.isFinite(numericPrice)
      ? `$${numericPrice.toLocaleString('es-MX', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : 'Sin precio'

  return [name, sku, formattedPrice].filter(Boolean).join(' · ')
}

const serverFieldMap: Record<string, keyof PromotionFormValues> = {
  name: 'name',
  description: 'description',
  type: 'type',
  channel: 'channel',
  scope_type: 'scope_type',
  value: 'value',
  fixed_application: 'fixed_application',
  buy_quantity: 'buy_quantity',
  pay_quantity: 'pay_quantity',
  starts_at: 'starts_at',
  ends_at: 'ends_at',
  is_active: 'is_active',
  product_ids: 'product_ids',
  excluded_product_ids: 'excluded_product_ids',
  category_ids: 'category_ids',
  department_ids: 'department_ids',
  sucursal_ids: 'sucursal_ids',
}

function normalizeSucursals(response: unknown): PromotionOption[] {
  if (!response || typeof response !== 'object') return []
  const envelope = response as {
    data?: unknown
    sucursales?: unknown
  }
  const firstLevel = envelope.data ?? envelope.sucursales ?? response

  if (Array.isArray(firstLevel)) return firstLevel as PromotionOption[]
  if (!firstLevel || typeof firstLevel !== 'object') return []

  const paginator = firstLevel as { data?: unknown; sucursales?: unknown }
  if (Array.isArray(paginator.data)) {
    return paginator.data as PromotionOption[]
  }
  if (Array.isArray(paginator.sucursales)) {
    return paginator.sucursales as PromotionOption[]
  }

  return []
}

function selectedCatalogProduct(value: unknown): PromotionOption | null {
  if (!value || typeof value !== 'object') return null
  const item = value as CatalogItem
  const id = Number(item.id)
  if (!Number.isInteger(id) || id <= 0) return null

  return {
    id,
    name: item.name || item.title || `Producto #${id}`,
    sku: typeof item.sku === 'string' ? item.sku : null,
  }
}

export function PromotionFormModal({
  open,
  promotionId,
  onOpenChange,
  onSaved,
}: PromotionFormModalProps) {
  const companyId = useSessionStore((state) => state.user?.company_id)
  const [products, setProducts] = useState<PromotionOption[]>([])
  const [excludedProducts, setExcludedProducts] = useState<PromotionOption[]>([])
  const [categories, setCategories] = useState<PromotionOption[]>([])
  const [departments, setDepartments] = useState<PromotionOption[]>([])
  const [sucursals, setSucursals] = useState<PromotionOption[]>([])
  const [applicabilityOpen, setApplicabilityOpen] = useState(false)
  const [branchSearch, setBranchSearch] = useState('')
  const [conflicts, setConflicts] = useState<PromotionConflict[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PromotionFormValues>({
    resolver: zodResolver(promotionFormSchema),
    defaultValues: emptyPromotionForm,
    mode: 'onChange',
  })

  const type = watch('type')
  const channel = watch('channel')
  const scopeType = watch('scope_type')
  const isActive = watch('is_active')
  const productIds = watch('product_ids')
  const sucursalIds = watch('sucursal_ids')

  useEffect(() => {
    if (!open) return
    let active = true

    const loadForm = async () => {
      setIsLoading(true)
      setLoadError(null)
      setConflicts([])
      setBranchSearch('')
      reset(emptyPromotionForm)
      setProducts([])
      setExcludedProducts([])
      setCategories([])
      setDepartments([])
      setApplicabilityOpen(false)

      try {
        const [branchResponse, promotion] = await Promise.all([
          sucursalService.getSucursales({
            company_id: companyId ?? undefined,
            per_page: 100,
          }),
          promotionId
            ? promotionService.get(promotionId)
            : Promise.resolve(null),
        ])

        if (!active) return
        setSucursals(normalizeSucursals(branchResponse))

        if (promotion) {
          reset(promotionToFormValues(promotion))
          setProducts(promotion.products)
          setExcludedProducts(promotion.excluded_products)
          setCategories(promotion.categories)
          setDepartments(promotion.departments)
        }
      } catch (error) {
        if (!active) return
        const message = isAxiosError<PromotionApiError>(error)
          ? error.response?.data.message
          : undefined
        setLoadError(
          message ||
            'No fue posible cargar los datos necesarios para la promoción.',
        )
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void loadForm()
    return () => {
      active = false
    }
  }, [companyId, open, promotionId, reloadKey, reset])

  useEffect(() => {
    if (type === 'percentage') {
      setValue('fixed_application', '')
      setValue('buy_quantity', '')
      setValue('pay_quantity', '')
    } else if (type === 'fixed_amount') {
      setValue('buy_quantity', '')
      setValue('pay_quantity', '')
    } else {
      setValue('value', '')
      setValue('fixed_application', '')
    }
  }, [setValue, type])

  useEffect(() => {
    if (scopeType === 'global') {
      setValue('sucursal_ids', [], { shouldValidate: true })
    }
  }, [scopeType, setValue])

  const filteredSucursals = useMemo(() => {
    const query = branchSearch.trim().toLocaleLowerCase('es-MX')
    if (!query) return sucursals
    return sucursals.filter((sucursal) =>
      sucursal.name.toLocaleLowerCase('es-MX').includes(query),
    )
  }, [branchSearch, sucursals])

  const addProduct = (value: unknown) => {
    const product = selectedCatalogProduct(value)
    if (!product || productIds.includes(product.id)) return

    setProducts((current) => [...current, product])
    setExcludedProducts((current) =>
      current.filter((currentProduct) => currentProduct.id !== product.id),
    )
    setValue(
      'excluded_product_ids',
      excludedProducts
        .filter((currentProduct) => currentProduct.id !== product.id)
        .map((currentProduct) => currentProduct.id),
      { shouldDirty: true, shouldValidate: true },
    )
    setValue('product_ids', [...productIds, product.id], {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const removeProduct = (id: number) => {
    setProducts((current) => current.filter((product) => product.id !== id))
    setValue(
      'product_ids',
      productIds.filter((productId) => productId !== id),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const syncProducts = (items: PromotionOption[]) => {
    setProducts(items)
    setValue(
      'product_ids',
      items.map((item) => item.id),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const syncExcludedProducts = (items: PromotionOption[]) => {
    setExcludedProducts(items)
    setValue(
      'excluded_product_ids',
      items.map((item) => item.id),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const syncCategories = (items: PromotionOption[]) => {
    setCategories(items)
    setValue(
      'category_ids',
      items.map((item) => item.id),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const syncDepartments = (items: PromotionOption[]) => {
    setDepartments(items)
    setValue(
      'department_ids',
      items.map((item) => item.id),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const toggleSucursal = (id: number) => {
    setValue(
      'sucursal_ids',
      sucursalIds.includes(id)
        ? sucursalIds.filter((sucursalId) => sucursalId !== id)
        : [...sucursalIds, id],
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const applyServerValidation = (apiError: PromotionApiError) => {
    let applied = false
    for (const [serverField, value] of Object.entries(apiError.errors ?? {})) {
      const field = serverFieldMap[serverField.split('.')[0]]
      const message = Array.isArray(value)
        ? value.find((item): item is string => typeof item === 'string')
        : typeof value === 'string'
          ? value
          : undefined

      if (field && message) {
        setError(field, { type: 'server', message })
        applied = true
      }
    }
    return applied
  }

  const submit = async (values: PromotionFormValues) => {
    setConflicts([])

    try {
      const payload = promotionFormToPayload(values)
      if (promotionId) {
        await promotionService.update(promotionId, payload)
        toast.success('Promoción actualizada exitosamente')
      } else {
        await promotionService.create(payload)
        toast.success('Promoción creada exitosamente')
      }

      onOpenChange(false)
      onSaved()
    } catch (error) {
      if (!isAxiosError<PromotionApiError>(error)) {
        toast.error('No fue posible guardar la promoción')
        return
      }

      const apiError = error.response?.data ?? {}
      const conflictData = apiError.errors?.conflicts
      if (Array.isArray(conflictData)) {
        setConflicts(conflictData as PromotionConflict[])
        return
      }

      if (applyServerValidation(apiError)) return
      toast.error(apiError.message || 'No fue posible guardar la promoción')
    }
  }

  const selectedSucursals = sucursals.filter((sucursal) =>
    sucursalIds.includes(sucursal.id),
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSubmitting) onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="text-primary h-5 w-5" />
            {promotionId ? 'Editar promoción' : 'Nueva promoción'}
          </DialogTitle>
          <DialogDescription>
            Define la regla, los productos, canales, sucursales y vigencia.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-muted-foreground flex min-h-64 items-center justify-center gap-2 text-sm">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando formulario...
          </div>
        ) : loadError ? (
          <div className="border-destructive/30 bg-destructive/5 space-y-4 rounded-lg border p-5">
            <p className="text-destructive text-sm">{loadError}</p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cerrar
              </Button>
              <Button
                type="button"
                onClick={() => setReloadKey((key) => key + 1)}
              >
                Reintentar
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(submit)} className="space-y-6">
            <PromotionConflictDetails
              conflicts={conflicts}
              products={products}
              sucursals={selectedSucursals}
            />

            <section className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <label htmlFor="promotion-name" className="text-sm font-medium">
                  Nombre <span className="text-destructive">*</span>
                </label>
                <Input
                  id="promotion-name"
                  placeholder="Ej. Semana de la salud"
                  {...register('name')}
                />
                <FormError message={errors.name?.message} />
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <label
                  htmlFor="promotion-description"
                  className="text-sm font-medium"
                >
                  Descripción
                </label>
                <Textarea
                  id="promotion-description"
                  rows={3}
                  placeholder="Describe el objetivo o condiciones internas de la promoción"
                  {...register('description')}
                />
                <FormError message={errors.description?.message} />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Tipo de promoción <span className="text-destructive">*</span>
                </label>
                <Select
                  value={type}
                  onValueChange={(value) =>
                    setValue('type', value as PromotionFormValues['type'], {
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger aria-label="Tipo de promoción">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje</SelectItem>
                    <SelectItem value="fixed_amount">Importe fijo</SelectItem>
                    <SelectItem value="buy_x_pay_y">
                      Compra X y paga Y
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormError message={errors.type?.message} />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Canal <span className="text-destructive">*</span>
                </label>
                <Select
                  value={channel}
                  onValueChange={(value) =>
                    setValue(
                      'channel',
                      value as PromotionFormValues['channel'],
                      {
                        shouldValidate: true,
                      },
                    )
                  }
                >
                  <SelectTrigger aria-label="Canal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pos">POS</SelectItem>
                    <SelectItem value="ecommerce">Ecommerce</SelectItem>
                    <SelectItem value="both">Ambos</SelectItem>
                  </SelectContent>
                </Select>
                <FormError message={errors.channel?.message} />
              </div>

              {(type === 'percentage' || type === 'fixed_amount') && (
                <div className="space-y-1.5">
                  <label
                    htmlFor="promotion-value"
                    className="text-sm font-medium"
                  >
                    {type === 'percentage' ? 'Porcentaje' : 'Importe de descuento al precio final'}{' '}
                    <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="promotion-value"
                    type="number"
                    min="0.01"
                    max={type === 'percentage' ? '100' : undefined}
                    step="0.01"
                    {...register('value')}
                  />
                  {type === 'fixed_amount' && (
                    <p className="text-xs text-muted-foreground">
                      El importe es el ahorro que verá el cliente e incluye impuestos.
                    </p>
                  )}
                  <FormError message={errors.value?.message} />
                </div>
              )}

              {type === 'fixed_amount' && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Aplicación del importe{' '}
                    <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={watch('fixed_application') || undefined}
                    onValueChange={(value) =>
                      setValue(
                        'fixed_application',
                        value as PromotionFormValues['fixed_application'],
                        { shouldValidate: true },
                      )
                    }
                  >
                    <SelectTrigger aria-label="Aplicación del importe">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_unit">Por unidad</SelectItem>
                      <SelectItem value="per_line">Por línea</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormError message={errors.fixed_application?.message} />
                </div>
              )}

              {type === 'buy_x_pay_y' && (
                <>
                  <div className="space-y-1.5">
                    <label
                      htmlFor="promotion-buy"
                      className="text-sm font-medium"
                    >
                      Cantidad a comprar{' '}
                      <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="promotion-buy"
                      type="number"
                      min="2"
                      step="1"
                      {...register('buy_quantity')}
                    />
                    <FormError message={errors.buy_quantity?.message} />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      htmlFor="promotion-pay"
                      className="text-sm font-medium"
                    >
                      Cantidad a pagar{' '}
                      <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="promotion-pay"
                      type="number"
                      min="1"
                      step="1"
                      {...register('pay_quantity')}
                    />
                    <FormError message={errors.pay_quantity?.message} />
                  </div>
                </>
              )}
            </section>

            <section className="border-border space-y-3 rounded-lg border p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="font-medium">
                    Aplicabilidad <span className="text-destructive">*</span>
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    Agrega productos directamente o configura categorías,
                    departamentos y exclusiones.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setApplicabilityOpen(true)}
                >
                  <PackageSearch className="mr-2 h-4 w-4" />
                  Administrar aplicabilidad
                </Button>
              </div>
              <CatalogSelect
                catalog="products"
                value=""
                returnObject
                fields={['id', 'name', 'sku', 'price']}
                onChange={addProduct}
                placeholder="Buscar producto por nombre o SKU..."
                itemLabelFormatter={formatPromotionProductOption}
              />
              {products.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {products.map((product) => (
                    <Badge
                      key={product.id}
                      variant="secondary"
                      className="gap-1.5 py-1.5"
                    >
                      {product.name}
                      {product.sku ? ` · ${product.sku}` : ''}
                      <button
                        type="button"
                        aria-label={`Quitar ${product.name}`}
                        onClick={() => removeProduct(product.id)}
                        className="hover:text-destructive rounded-sm"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">
                  {categories.length}{' '}
                  {categories.length === 1 ? 'categoría' : 'categorías'}
                </Badge>
                <Badge variant="outline">
                  {departments.length}{' '}
                  {departments.length === 1
                    ? 'departamento'
                    : 'departamentos'}
                </Badge>
                <Badge variant="outline">
                  {excludedProducts.length}{' '}
                  {excludedProducts.length === 1 ? 'exclusión' : 'exclusiones'}
                </Badge>
              </div>
              <FormError message={errors.product_ids?.message} />
              <FormError message={errors.excluded_product_ids?.message} />
            </section>

            <section className="border-border space-y-4 rounded-lg border p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Alcance <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={scopeType}
                    onValueChange={(value) =>
                      setValue(
                        'scope_type',
                        value as PromotionFormValues['scope_type'],
                        { shouldValidate: true },
                      )
                    }
                  >
                    <SelectTrigger aria-label="Alcance">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global</SelectItem>
                      <SelectItem value="branches">
                        Sucursales seleccionadas
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormError message={errors.scope_type?.message} />
                </div>

                <div className="flex items-end pb-1">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                    <Checkbox
                      checked={isActive}
                      onCheckedChange={(checked) =>
                        setValue('is_active', checked === true, {
                          shouldValidate: true,
                        })
                      }
                    />
                    Crear promoción activa
                  </label>
                </div>
              </div>

              {scopeType === 'branches' && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    Sucursales <span className="text-destructive">*</span>
                  </p>
                  <Input
                    type="search"
                    value={branchSearch}
                    onChange={(event) => setBranchSearch(event.target.value)}
                    placeholder="Buscar sucursal..."
                    aria-label="Buscar sucursal"
                  />
                  <div className="border-border max-h-52 overflow-y-auto rounded-md border">
                    {filteredSucursals.length > 0 ? (
                      filteredSucursals.map((sucursal, index) => {
                        const checked = sucursalIds.includes(sucursal.id)
                        return (
                          <label
                            key={sucursal.id}
                            className={`hover:bg-table-row-hover flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm ${index > 0 ? 'border-border border-t' : ''}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() =>
                                toggleSucursal(sucursal.id)
                              }
                            />
                            <span className="flex-1">{sucursal.name}</span>
                            {checked && (
                              <Check className="text-primary h-4 w-4" />
                            )}
                          </label>
                        )
                      })
                    ) : (
                      <p className="text-muted-foreground p-4 text-center text-sm">
                        No se encontraron sucursales.
                      </p>
                    )}
                  </div>
                  <FormError message={errors.sucursal_ids?.message} />
                </div>
              )}
            </section>

            <section className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="promotion-start"
                  className="text-sm font-medium"
                >
                  Inicio de vigencia <span className="text-destructive">*</span>
                </label>
                <Input
                  id="promotion-start"
                  type="date"
                  {...register('starts_at')}
                />
                <FormError message={errors.starts_at?.message} />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="promotion-end" className="text-sm font-medium">
                  Fin de vigencia <span className="text-destructive">*</span>
                </label>
                <Input
                  id="promotion-end"
                  type="date"
                  {...register('ends_at')}
                />
                <FormError message={errors.ends_at?.message} />
              </div>
              <p className="text-muted-foreground text-xs md:col-span-2">
                La promoción se aplicará durante todo el día, desde el inicio de
                la primera fecha hasta el final de la última.
              </p>
            </section>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {promotionId ? 'Guardar cambios' : 'Crear promoción'}
              </Button>
            </DialogFooter>
          </form>
        )}
        <PromotionApplicabilitySheet
          open={applicabilityOpen}
          onOpenChange={setApplicabilityOpen}
          products={products}
          excludedProducts={excludedProducts}
          categories={categories}
          departments={departments}
          onProductsChange={syncProducts}
          onExcludedProductsChange={syncExcludedProducts}
          onCategoriesChange={syncCategories}
          onDepartmentsChange={syncDepartments}
        />
      </DialogContent>
    </Dialog>
  )
}
